import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { 
    getUsersInHeadquarter, 
    createMultipleTrainingPlans,
    getHeadsetsForAdmin,
    getHeadsets
} from "../helpers/requests";
import { useAuth } from "../../firebase/auth.tsx";

const CreateTrainingPlanModal = ({
  setShowCreatePlanModal,
  handleCreatePlan,
  headquarterId: initialHeadquarterId,
}) => {
  const { t } = useTranslation();
  const { userRole, userId, headquarterId: authHeadquarterId } = useAuth();
  
  const [plans, setPlans] = useState([
    {
      id: Date.now().toString(),
      headsetId: "",
      userId: "",
      expirationDate: "",
      headquarterId: "" // Store internally to look up users
    }
  ]);

  const [availableHeadsets, setAvailableHeadsets] = useState<any[]>([]);
  const [usersCache, setUsersCache] = useState<{ [key: string]: any[] }>({});
  
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        let rawHeadsets = [];
        const effectiveHQId = initialHeadquarterId || (userRole === "headquarterAdmin" ? authHeadquarterId : null);

        if (effectiveHQId) {
            rawHeadsets = await getHeadsets(effectiveHQId);
        } else if (userRole === "headsetAdmin") {
            rawHeadsets = await getHeadsetsForAdmin(userId);
        } else if (userRole === "admin") {
            rawHeadsets = await getHeadsets();
        }

        const filteredHeadsets = rawHeadsets
            .filter((h: any) => h.licenseId)
            .map((h: any) => ({
                id: h.id, // Headset Document ID (needed for selection)
                licenseId: h.licenseId, 
                headsetName: h.name || h.deviceId,
                headquarterName: h.headquarterName,
                deviceId: h.deviceId,
                headquarterId: h.headquarterId
            }));
        
        setAvailableHeadsets(filteredHeadsets);
        
      } catch (error) {
        console.error("Error fetching headsets for training plan:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [initialHeadquarterId, userRole, userId, authHeadquarterId]);

  const fetchUsersForHQ = async (hqId) => {
    if (!hqId || usersCache[hqId]) return;
    try {
      const users = await getUsersInHeadquarter(hqId);
      setUsersCache((prev) => ({ ...prev, [hqId]: users }));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const addPlanRow = () => {
    setPlans([
      ...plans,
      {
        id: Date.now().toString() + Math.random(),
        headsetId: "",
        userId: "",
        expirationDate: "",
        headquarterId: ""
      },
    ]);
  };

  const removePlanRow = (id) => {
    if (plans.length > 1) {
      setPlans(plans.filter((p) => p.id !== id));
    }
  };

  const updatePlanField = (id, field, value) => {
    setPlans((prevPlans) =>
      prevPlans.map((p) => {
        if (p.id === id) {
          const updatedPlan = { ...p, [field]: value };

          if (field === "headsetId") {
            const selectedHeadset = availableHeadsets.find(h => h.id === value);
            updatedPlan.userId = ""; // Reset user when headset changes
            if (selectedHeadset) {
                updatedPlan.headquarterId = selectedHeadset.headquarterId;
                fetchUsersForHQ(selectedHeadset.headquarterId);
            } else {
                updatedPlan.headquarterId = "";
            }
          }

          return updatedPlan;
        }
        return p;
      })
    );
    setFormSubmitted(false);
    setErrorMessage("");
  };

  const validatePlan = (plan) => {
      if (!plan.headsetId) return false;
      if (!plan.userId) return false;
      return true;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormSubmitted(true);
    setErrorMessage("");

    for (const plan of plans) {
        if (!validatePlan(plan)) return;
        
        if (plan.expirationDate) {
            const year = new Date(plan.expirationDate).getFullYear();
            if (year > 9999) {
                setErrorMessage("Invalid_date_year_too_large");
                return;
            }
        }
    }

    setIsLoading(true);

    try {
        const payload = plans.map(p => {
            const headset = availableHeadsets.find(h => h.id === p.headsetId);
            return {
                licenseId: headset.licenseId,
                userId: p.userId,
                expirationDate: p.expirationDate === "" ? null : p.expirationDate,
                logoutAfterTraining: false // Removed option, default false
            };
        });

        const response = await createMultipleTrainingPlans(payload);
        
        if (response && response.results) {
            // Enrich results with names for the UI
            const createdPlans = response.results.map((newPlan, index) => {
                const originalPlan = plans[index];
                const headset = availableHeadsets.find(h => h.id === originalPlan.headsetId);
                const users = usersCache[originalPlan.headquarterId] || [];
                const user = users.find(u => u.id === originalPlan.userId);
                
                return {
                    ...newPlan,
                    userName: user ? `${user.firstName} ${user.lastName}` : "Unknown",
                    headsetName: headset ? headset.headsetName : "Unknown",
                    headquarterName: headset ? headset.headquarterName : ""
                };
            });
            
            handleCreatePlan(createdPlans);
            setShowCreatePlanModal(false);
        } else {
             // Handle partial error or empty response if needed
             setErrorMessage("Error creating plans");
        }

    } catch (error) {
        console.error("Failed to create training plans:", error);
        setErrorMessage("An error occurred");
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <div className="confirmation-modal">
      <div
        className="modal-content"
        style={{
          width: "90vw",
          maxWidth: "1000px",
          height: "80vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            textAlign: "center",
            fontSize: "1.3em",
            marginTop: "1vh",
            marginBottom: "2vh",
          }}
        >
          {t("Create_new_training_plans")}
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              padding: "0 10px",
              marginBottom: "5px",
              fontWeight: "bold",
              color: "#404040",
            }}
          >
            <div style={{ flex: 2, marginRight: "10px" }}>{t("Headset")}</div>
            <div style={{ flex: 2, marginRight: "10px" }}>{t("User")}</div>
            <div style={{ flex: 1.5, marginRight: "10px" }}>{t("Expiration_Date")}</div>
            <div style={{ width: "40px" }}></div>
          </div>

          {/* Rows */}
          <div style={{ flex: 1, overflowY: "auto", padding: "10px 4px" }}>
            {plans.map((plan) => (
              <div
                key={plan.id}
                style={{
                  display: "flex",
                  marginBottom: "10px",
                  alignItems: "start",
                }}
              >
                {/* Headset Select */}
                <div style={{ flex: 2, marginRight: "10px" }}>
                  <select
                    className="form-control"
                    value={plan.headsetId}
                    onChange={(e) => updatePlanField(plan.id, "headsetId", e.target.value)}
                    disabled={isLoading}
                    style={{
                      backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                      borderRadius: "10px",
                      color: "#404040",
                      height: "35px",
                      paddingLeft: "10px",
                    }}
                  >
                    <option value="" disabled hidden></option>
                    {availableHeadsets.map((h) => (
                      <option key={h.id} value={h.id}>
                        {h.headsetName} ({h.headquarterName})
                      </option>
                    ))}
                  </select>
                  {formSubmitted && !plan.headsetId && (
                    <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                      {t("Required")}
                    </div>
                  )}
                </div>

                {/* User Select */}
                <div style={{ flex: 2, marginRight: "10px" }}>
                  <select
                    className="form-control"
                    value={plan.userId}
                    onChange={(e) => updatePlanField(plan.id, "userId", e.target.value)}
                    disabled={isLoading || !plan.headsetId}
                    style={{
                      backgroundColor: isLoading || !plan.headsetId ? "#E0E0E0" : "#CCCCCC",
                      borderRadius: "10px",
                      color: "#404040",
                      height: "35px",
                      paddingLeft: "10px",
                    }}
                  >
                    <option value="" disabled hidden>
                         {plan.headsetId ? "" : t("Select_headset_first")}
                    </option>
                    {(usersCache[plan.headquarterId] || []).map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName} ({u.email})
                      </option>
                    ))}
                  </select>
                  {formSubmitted && !plan.userId && plan.headsetId && (
                    <div style={{ color: "#A63939", fontSize: "0.8em" }}>
                      {t("Required")}
                    </div>
                  )}
                </div>

                {/* Expiration Date */}
                <div style={{ flex: 1.5, marginRight: "10px" }}>
                  <input
                    type="date"
                    className="form-control"
                    value={plan.expirationDate}
                    onChange={(e) => updatePlanField(plan.id, "expirationDate", e.target.value)}
                    disabled={isLoading}
                    style={{
                      backgroundColor: isLoading ? "#E0E0E0" : "#CCCCCC",
                      borderRadius: "10px",
                      color: "#404040",
                      height: "35px",
                      paddingLeft: "10px",
                    }}
                  />
                </div>

                {/* Remove Button */}
                <div
                  style={{
                    width: "40px",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  {plans.length > 1 && (
                    <button
                      type="button"
                      disabled={isLoading}
                      onClick={() => removePlanRow(plan.id)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: isLoading ? "#ccc" : "#A63939",
                        fontWeight: "bold",
                        cursor: isLoading ? "default" : "pointer",
                        fontSize: "1.2em",
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Add Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "10px",
            }}
          >
            <button
              type="button"
              disabled={isLoading}
              onClick={addPlanRow}
              className="btn btn-secondary"
              style={{
                backgroundColor: isLoading ? "#7ab09e" : "#39a686",
                borderColor: isLoading ? "#7ab09e" : "#39a686",
                color: "white",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "1.5em",
                padding: 0,
                paddingBottom: "4px",
                lineHeight: "1",
                cursor: isLoading ? "default" : "pointer",
              }}
            >
              +
            </button>
          </div>

          {errorMessage && (
            <div
              style={{
                textAlign: "center",
                color: "#A63939",
                fontSize: "0.8em",
                margin: "1vh 0",
              }}
            >
              {t(errorMessage)}
            </div>
          )}

          <div className="button-container">
            <button
              type="button"
              className="buttonCancel btn btn-primary"
              disabled={isLoading}
              onClick={() => setShowCreatePlanModal(false)}
            >
              {t("Cancel")}
            </button>
            <button
              type="submit"
              className="btn btn-primary buttonAcceptGreen"
              disabled={isLoading}
            >
              {isLoading ? t("Loading...") : t("Create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTrainingPlanModal;